import React, { PropsWithChildren, createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import ToastStack, { ToastAction, ToastMessage } from '../components/ToastStack'

type ReportProcessToast = {
  id: string
  toastId: number
  title?: string
  message: React.ReactNode
  variant: 'info' | 'success' | 'danger'
  dismissible: boolean
  autoDismissMs?: number
  action?: ToastAction
}

type ReportProcessStart = {
  title?: string
  message: React.ReactNode
}

type ReportProcessUpdate = {
  title?: string
  message: React.ReactNode
}

type ReportProcessComplete = {
  title?: string
  message: React.ReactNode
  action?: ToastAction
  autoDismissMs?: number
}

type ReportProcessFailure = {
  title?: string
  message: React.ReactNode
  autoDismissMs?: number
}

interface ReportGenerationToastContextValue {
  startProcess: (options: ReportProcessStart) => string
  updateProcess: (processId: string, options: ReportProcessUpdate) => void
  completeProcess: (processId: string, options: ReportProcessComplete) => void
  failProcess: (processId: string, options: ReportProcessFailure) => void
  dismissProcess: (processId: string) => void
}

const ReportGenerationToastContext = createContext<ReportGenerationToastContextValue | undefined>(undefined)

const removeProcess = (items: Record<string, ReportProcessToast>, processId: string) => {
  if (!items[processId]) {
    return items
  }
  const next = { ...items }
  delete next[processId]
  return next
}

export const ReportGenerationToastProvider: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const [processes, setProcesses] = useState<Record<string, ReportProcessToast>>({})
  const processIdRef = useRef(0)
  const toastIdRef = useRef(10000)

  const startProcess = useCallback((options: ReportProcessStart) => {
    const nextProcessId = `report-process-${Date.now()}-${processIdRef.current++}`
    const nextToastId = toastIdRef.current++

    setProcesses((current) => ({
      ...current,
      [nextProcessId]: {
        id: nextProcessId,
        toastId: nextToastId,
        title: options.title,
        message: options.message,
        variant: 'info',
        dismissible: false,
      },
    }))

    return nextProcessId
  }, [])

  const updateProcess = useCallback((processId: string, options: ReportProcessUpdate) => {
    setProcesses((current) => {
      const target = current[processId]
      if (!target) {
        return current
      }
      return {
        ...current,
        [processId]: {
          ...target,
          title: options.title ?? target.title,
          message: options.message,
          variant: 'info',
          dismissible: false,
          autoDismissMs: undefined,
          action: undefined,
        },
      }
    })
  }, [])

  const completeProcess = useCallback((processId: string, options: ReportProcessComplete) => {
    setProcesses((current) => {
      const target = current[processId]
      if (!target) {
        return current
      }
      return {
        ...current,
        [processId]: {
          ...target,
          title: options.title ?? target.title,
          message: options.message,
          variant: 'success',
          dismissible: true,
          autoDismissMs: options.autoDismissMs ?? (options.action ? 12000 : 8000),
          action: options.action,
        },
      }
    })
  }, [])

  const failProcess = useCallback((processId: string, options: ReportProcessFailure) => {
    setProcesses((current) => {
      const target = current[processId]
      if (!target) {
        return current
      }
      return {
        ...current,
        [processId]: {
          ...target,
          title: options.title ?? target.title,
          message: options.message,
          variant: 'danger',
          dismissible: true,
          autoDismissMs: options.autoDismissMs ?? 12000,
          action: undefined,
        },
      }
    })
  }, [])

  const dismissProcess = useCallback((processId: string) => {
    setProcesses((current) => removeProcess(current, processId))
  }, [])

  const toasts = useMemo<ToastMessage[]>(
    () =>
      Object.values(processes)
        .sort((left, right) => left.toastId - right.toastId)
        .map((process) => ({
          id: process.toastId,
          title: process.title,
          message: process.message,
          variant: process.variant,
          dismissible: process.dismissible,
          autoDismissMs: process.autoDismissMs,
          action: process.action,
        })),
    [processes]
  )

  const handleDismissToast = useCallback((toastId: number) => {
    setProcesses((current) => {
      const match = Object.entries(current).find(([, process]) => process.toastId === toastId)
      if (!match) {
        return current
      }
      return removeProcess(current, match[0])
    })
  }, [])

  const value = useMemo<ReportGenerationToastContextValue>(
    () => ({
      startProcess,
      updateProcess,
      completeProcess,
      failProcess,
      dismissProcess,
    }),
    [completeProcess, dismissProcess, failProcess, startProcess, updateProcess]
  )

  return (
    <ReportGenerationToastContext.Provider value={value}>
      {children}
      <ToastStack toasts={toasts} onDismiss={handleDismissToast} />
    </ReportGenerationToastContext.Provider>
  )
}

export const useReportGenerationToasts = () => {
  const context = useContext(ReportGenerationToastContext)
  if (!context) {
    throw new Error('useReportGenerationToasts must be used within ReportGenerationToastProvider')
  }
  return context
}
